import React, { useState } from 'react';
import { Video, X } from 'lucide-react';

interface YouTubeEmbedProps {
  url: string;
  title: string;
  className?: string;
}

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({ url, title, className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract YouTube video ID from URL
  const getYouTubeId = (url: string): string | null => {
    if (!url) return null;
    
    // Handle various YouTube URL formats
    const patterns = [
      /youtu\.be\/([^#&?]*)/,
      /youtube\.com\/watch\?v=([^#&?]*)/,
      /youtube\.com\/embed\/([^#&?]*)/,
      /youtube\.com\/v\/([^#&?]*)/,
      /youtube\.com\/shorts\/([^#&?]*)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    return null;
  };

  const videoId = getYouTubeId(url);

  if (!videoId) {
    // If not a YouTube URL, render as regular video link
    return (
      <div className={`relative ${className}`}>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="flex items-center justify-center w-full h-32 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
        >
          <Video className="text-red-600 mr-2" size={20} />
          <span className="text-red-600 font-medium">Watch Video</span>
        </a>
      </div>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoId}`;
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  if (!isExpanded) {
    return (
      <div className={`relative ${className}`}>
        <div 
          className="cursor-pointer group"
          onClick={() => setIsExpanded(true)}
        >
          <img 
            src={thumbnailUrl}
            alt={title}
            className="w-full h-32 object-cover rounded-lg"
            onError={(e) => {
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
            <div className="bg-red-600 text-white p-3 rounded-full">
              <Video size={24} />
            </div>
          </div>
          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
            YouTube
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={embedUrl}
          title={title}
          className="absolute top-0 left-0 w-full h-full rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <button
        onClick={() => setIsExpanded(false)}
        className="absolute top-2 right-2 bg-black/70 text-white p-1 rounded hover:bg-black/80 transition-colors"
        title="Close video"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default YouTubeEmbed;
